/**
 * KaTeX math extraction + render.
 *
 * Math syntax is pulled OUT of the raw markdown source before markdown-it
 * tokenises it, replaced with sentinel placeholders like ` MATHPH7 `, and
 * the rendered KaTeX HTML is spliced back AFTER DOMPurify runs. Two reasons
 * for the placeholder dance:
 *
 *   1. KaTeX output is a deeply nested tree of <span class="katex"> + MathML
 *      + custom attributes. Letting DOMPurify scrub it doubles the work and
 *      risks dropping styling hooks we explicitly allowed in sanitize.ts.
 *   2. Markdown-it eats $ characters inside inline content (turns them into
 *      smart quotes via typographer, escapes backslashes inside fenced code,
 *      etc.). Extracting first means KaTeX receives the user's exact TeX.
 *
 * Three patterns recognised, in this order (longest match wins):
 *
 *   ```math\n…\n```         -> displayMode KaTeX block
 *   $$…$$ (allow newlines)  -> displayMode KaTeX block
 *   $…$ (single line)       -> inline KaTeX span
 *
 * KaTeX parse failures fall back to the original markdown source verbatim
 * (throwOnError: false + a try/catch outer guard). The renderer never
 * throws, so a typo in one equation doesn't blank the whole document.
 *
 * Use `string.match(re)` per repo convention; FENCED_RE / DISPLAY_RE /
 * INLINE_RE are single-pass replacers so we use String.prototype.replace
 * with the same regexes.
 *
 * Loading strategy
 * ----------------
 * KaTeX is ~150 KB gzipped — too heavy to pay on every cold start when
 * most pasted markdown contains no math at all. We defer the actual KaTeX
 * import until the first time `extractMath` finds at least one math region,
 * then cache the module reference for subsequent renders. The render
 * pipeline stays sync: we substitute placeholders eagerly, then schedule
 * a post-render swap via `primeMath()` which the consumer awaits and
 * re-runs render() once KaTeX is in cache. For the first render with
 * math, callers get the raw TeX (or a "Loading math..." marker) until
 * KaTeX lands; for every subsequent render the cached module is used
 * synchronously and the math renders inline.
 */

type Katex = typeof import('katex')['default']

let katexPromise: Promise<Katex> | null = null
let katexCached: Katex | null = null

/**
 * Kick off the KaTeX import. Cached promise so concurrent renders don't
 * trigger two engine boots. Returns the module; subsequent calls resolve
 * from the cache.
 */
export async function primeMath(): Promise<Katex> {
  if (katexCached) return katexCached
  if (!katexPromise) {
    katexPromise = import('katex').then((mod) => {
      katexCached = mod.default
      return katexCached
    })
  }
  return katexPromise
}

// ` ```math\n<tex>\n``` ` — fenced block, body is everything until the next
// closing fence. Non-greedy on the body so multiple math blocks in one
// document don't collapse together.
const FENCED_RE = /```math\s*\n([\s\S]*?)\n```/g

// `$$<tex>$$` — display math. Allow internal newlines so multi-line equations
// work. Non-greedy.
const DISPLAY_RE = /\$\$([\s\S]+?)\$\$/g

// `$<tex>$` — inline math. Single line only and the body cannot start or end
// with whitespace (the GitHub + Pandoc convention; prevents `$5 and $10`
// money snippets from accidentally rendering as math). Body must also
// contain no $ so the regex doesn't span two unrelated inline spans.
const INLINE_RE = /(^|[^\\$])\$(?!\s)([^\n$]+?)(?<!\s)\$(?!\d)/g

// P1.4 from the 2026-05-23 codex audit: the old placeholder was a fixed
// `MATHPH<N>` token. A user who literally wrote "MATHPH1" in their source
// would have it replaced with the Nth math expression's KaTeX HTML — rare
// but real, and worse with html:true now in play (P0 #1).
//
// Fix: per-render nonce. Each extractMath() call generates a fresh random
// nonce, embeds it in both the placeholder format AND the restore regex,
// and returns a `restore` closure that uses the captured nonce. Two
// concurrent renders (unlikely but possible — pasted long doc + share-URL
// landing) can't cross-contaminate either.
const PLACEHOLDER_PREFIX = '__MD_MATH_'
const PLACEHOLDER_SUFFIX = '__'

function makeNonce(): string {
  // 8 chars of base36 — ~10^12 keyspace, well beyond the spoofing
  // threshold. Crypto-random would be overkill; the threat model is
  // "user accidentally types a literal placeholder", not an attacker
  // crafting one.
  return Math.random().toString(36).slice(2, 10)
}

// Wrap the placeholder in spaces so markdown-it treats it as plain text
// even when it lands at a paragraph edge or beside punctuation.
function wrap(nonce: string, i: number): string {
  return ` ${PLACEHOLDER_PREFIX}${nonce}_${i}${PLACEHOLDER_SUFFIX} `
}
function makeRestoreRe(nonce: string): RegExp {
  // Escape the nonce defensively even though base36 has no regex
  // metacharacters; future-proofs against a switch to a richer alphabet.
  const escaped = nonce.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(
    `\\s?${PLACEHOLDER_PREFIX}${escaped}_(\\d+)${PLACEHOLDER_SUFFIX}\\s?`,
    'g',
  )
}

function renderKatexSync(tex: string, displayMode: boolean): string {
  // If KaTeX is already in cache (every render after the first math-bearing
  // one), render synchronously into HTML.
  if (katexCached) {
    try {
      return katexCached.renderToString(tex, {
        displayMode,
        throwOnError: false,
        output: 'html',
      })
    } catch {
      // Fall through to the loading-placeholder below.
    }
  }
  // First-render placeholder while KaTeX is loading — the source TeX is
  // shown in a styled wrapper so the reader still sees their input. The
  // consumer re-renders once primeMath() resolves and the cache is warm.
  const escaped = tex
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return displayMode
    ? `<div class="katex-error">${escaped}</div>`
    : `<span class="katex-error">${escaped}</span>`
}

export interface ExtractMathResult {
  /** Source with math regions replaced by ` MATHPH<N> ` tokens. */
  protected: string
  /** Splice the rendered KaTeX HTML back into a string of HTML. */
  restore: (html: string) => string
  /** True when the source contained at least one math region. */
  hasMath: boolean
}

export function extractMath(source: string): ExtractMathResult {
  const nonce = makeNonce()
  const restoreRe = makeRestoreRe(nonce)
  const fragments: string[] = []
  const push = (html: string): string => {
    const idx = fragments.length
    fragments.push(html)
    return wrap(nonce, idx)
  }

  // Order matters: fenced first (longest match), then display, then inline.
  let working = source.replace(FENCED_RE, (_full, body: string) =>
    push(renderKatexSync(body, true)),
  )
  working = working.replace(DISPLAY_RE, (_full, body: string) =>
    push(renderKatexSync(body, true)),
  )
  working = working.replace(INLINE_RE, (_full, prefix: string, body: string) =>
    prefix + push(renderKatexSync(body, false)),
  )

  const hasMath = fragments.length > 0
  // Kick off the KaTeX import the moment we detect math — by the time the
  // consumer re-renders, the cache is warm and renderKatexSync above produces
  // real HTML.
  if (hasMath && !katexCached) {
    void primeMath()
  }

  return {
    protected: working,
    hasMath,
    restore: (html: string) =>
      html.replace(restoreRe, (_match, n: string) => {
        const i = Number(n)
        return fragments[i] ?? ''
      }),
  }
}

/**
 * Test-only convenience: extract + immediately restore on the same source,
 * skipping markdown-it. Real consumers go through render() which interleaves
 * markdown-it + sanitize between extract and restore.
 */
export function renderMath(source: string): string {
  const { protected: prot, restore } = extractMath(source)
  return restore(prot)
}
